import React, { useEffect } from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";

import { Error, Header, Input, InputLabel, Loading } from "components";
import Items from "./Items";
import DetailsTemplate from "screens/components/DetailsTemplate";

import { useForm, Controller } from "react-hook-form";

import { strings } from "assets/constants";
import {
  useAttribute,
  useAddAttribute,
  useEditAttribute,
  useDeleteAttribute,
} from "clients/apis";
import { alertPermission, mq } from "utils";
import { useChmod } from "hooks";

export default function Attribute() {
  const { chmod } = useChmod();

  const { id } = useRoute<RootRouteProps<"attribute">>().params;

  const headerTitle = id ? strings?.edit_attr : strings.add_attr;

  const { isLoading, isError, refetch, isRefetching, error, data } =
    useAttribute(id);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { title: "", items: [] },
  });

  useEffect(() => {
    if (data) {
      setValue("title", data?.data?.title);
      setValue("items", data?.data?.items);
    }
  }, [data, setValue]);

  const addAttrMut = useAddAttribute();
  const editAttrMut = useEditAttribute();
  const deleteAttrMut = useDeleteAttribute();

  const _onSubmit = () => {
    const postData: AttributeReqT = {
      title: getValues().title,
    };
    getValues()?.items?.length > 0 &&
      Object.assign(postData, {
        items: getValues()?.items?.map((item: AttributeItemT) => ({
          value: item.value,
          is_customized: item.is_customized || 0,
        })),
      });

    addAttrMut.mutate(postData);
  };

  const _onEdit = () => {
    const postData: AttributeReqT = {
      title: getValues().title,
    };
    getValues().items.length > 0 &&
      Object.assign(postData, {
        items: getValues().items.map((item: AttributeItemT) => ({
          id: item?.id || null,
          attribute_id: data?.data?.id,
          value: item?.value,
          is_customized: item?.is_customized || 0,
        })),
      });

    editAttrMut.mutate({ id: data?.data?.id, data: postData });
  };

  const _onDeleteConfirm = (close: () => void) =>
    deleteAttrMut.mutate(data?.data.id, { onSuccess: close });

  const _renderContent = () => {
    if (id && isLoading) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }
    return (
      <DetailsTemplate
        onDelete={_onDeleteConfirm}
        deletable={data && chmod("attribute", "DELETE") ? true : false}
        deleteLoading={deleteAttrMut?.isLoading}
        deleteDescription={strings.delete_attribute_description}
        submitLabel={!data ? strings.submit_info : strings.edit_info}
        submitLoading={addAttrMut?.isLoading || editAttrMut?.isLoading}
        onSubmit={
          data
            ? chmod("attribute", "PUT")
              ? handleSubmit(_onEdit)
              : alertPermission
            : chmod("attribute", "POST")
            ? handleSubmit(_onSubmit)
            : alertPermission
        }
      >
        <>
          <InputLabel label={strings.title} />
          <Controller
            control={control}
            name="title"
            rules={{
              required: { value: true, message: strings.fill_input },
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder={strings.insert_title}
                icon={{ name: "edit-3" }}
                error={errors.title?.message as string}
              />
            )}
          />
        </>

        <>
          <InputLabel label={strings.items} />
          <Controller
            control={control}
            name="items"
            render={({ field: { value, onChange } }) => (
              <Items items={value} onChange={onChange} />
            )}
          />
        </>
      </DetailsTemplate>
    );
  };

  return (
    <>
      <Header title={headerTitle} />
      <>{_renderContent()}</>
    </>
  );
}
